import React, { createContext, useState, useCallback, useMemo, useContext } from 'react';
import { issueOptionsService } from '../services/IssueOptionsService';

export const IssueOptionsContext = createContext();

const emptyKind = { items: [], loading: false, error: false };

const initialState = {
    priorities: emptyKind,
    statuses: emptyKind,
    types: emptyKind,
};

const sortByName = items =>
    [...items].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }));

export const IssueOptionsProvider = props => {
    const [state, setState] = useState(initialState);

    const getOptions = useCallback(async kind => {
        setState(prev => ({ ...prev, [kind]: { ...prev[kind], loading: true, error: false } }));
        try {
            const res = await issueOptionsService.getOptions(kind);
            setState(prev => ({ ...prev, [kind]: { items: res?.data ?? [], loading: false, error: false } }));
        } catch (e) {
            setState(prev => ({ ...prev, [kind]: { ...prev[kind], loading: false, error: true } }));
        }
    }, []);

    const createOption = useCallback(async (kind, name) => {
        const res = await issueOptionsService.createOption(kind, name);
        const entity = res?.data;
        if (entity) {
            setState(prev => ({
                ...prev,
                [kind]: { ...prev[kind], items: sortByName([...prev[kind].items, entity]) },
            }));
        }
        return entity;
    }, []);

    const updateOption = useCallback(async (kind, id, name) => {
        const res = await issueOptionsService.updateOption(kind, id, name);
        const entity = res?.data;
        if (entity) {
            setState(prev => ({
                ...prev,
                [kind]: {
                    ...prev[kind],
                    items: sortByName(prev[kind].items.map(item => (item.id === id ? entity : item))),
                },
            }));
        }
        return entity;
    }, []);

    const deleteOption = useCallback(async (kind, id) => {
        await issueOptionsService.deleteOption(kind, id);
        setState(prev => ({
            ...prev,
            [kind]: { ...prev[kind], items: prev[kind].items.filter(item => item.id !== id) },
        }));
    }, []);

    const getUsage = useCallback(async (kind, id) => {
        const res = await issueOptionsService.getUsage(kind, id);
        return res?.data;
    }, []);

    const values = useMemo(
        () => ({
            issueOptions: state,
            getOptions,
            createOption,
            updateOption,
            deleteOption,
            getUsage,
        }),
        [state, getOptions, createOption, updateOption, deleteOption, getUsage]
    );

    return <IssueOptionsContext.Provider value={values}>{props.children}</IssueOptionsContext.Provider>;
};

export const useIssueOptions = () => {
    const context = useContext(IssueOptionsContext);
    if (context === undefined) {
        throw new Error('useIssueOptions must be used within an IssueOptionsProvider component.');
    }
    return context;
};
